import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import FirebaseActivityService from '../services/firebase/FirebaseActivityService';
import { db } from '../config/firebase';
import TeamMemberService from '../services/TeamMemberService';
import CompanySettingsService from '../services/CompanySettingsService';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import SettingsPageNew from '../components/SettingsPageNew';
import SessionTimer from '../components/SessionTimer';
import PrescriptionsPage from '../components/PrescriptionsPage';
import OverviewTab from '../components/dashboard/provider/OverviewTab';
import ClientsTab from '../components/dashboard/provider/ClientsTab';
import MeetingsTab from '../components/dashboard/provider/MeetingsTab';
import SessionsTab from '../components/dashboard/provider/SessionsTab';
import ServicesTab from '../components/dashboard/provider/ServicesTab';
import ScheduleTab from '../components/dashboard/provider/ScheduleTab';
import BillingTab from '../components/dashboard/provider/BillingTab';
import ActivityTab from '../components/dashboard/provider/ActivityTab';
import MarketingTab from '../components/dashboard/provider/MarketingTab';
import AnalyticsTab from '../components/dashboard/provider/AnalyticsTab';
import TasksTab from '../components/dashboard/provider/TasksTab';
import InquiriesTab from '../components/dashboard/provider/InquiriesTab';
import HRTab from '../components/dashboard/provider/HRTab';
import MessagesTab from '../components/dashboard/provider/MessagesTab';
import TeamChatTab from '../components/dashboard/provider/TeamChatTab';
import ClientActivityTab from '../components/dashboard/provider/ClientActivityTab';
import EmergencyCancellationsTab from '../components/dashboard/provider/EmergencyCancellationsTab';
import VoiceControls from '../components/dashboard/shared/VoiceControls';
import DictationPanel from '../components/dictation/DictationPanel';
// FloatingHopeButton is client-only — provider side uses Clinic voice via useVoiceCommands
import VideoSessionWhereby from '../components/VideoSessionWhereby';
import PostSessionReviewModal from '../components/PostSessionReviewModal';
import { PostSessionResult } from '../services/PostSessionPipelineService';
import ProfileSettings from '../components/ProfileSettings';
import { PICOVOICE_CONFIG } from '../config/picovoice';
import { Patient as PatientServiceType } from '../services/firebase/FirebasePatientService';
import { firebaseAppointmentService, Appointment } from '../services/firebase/FirebaseAppointmentService';
import NotificationTriggerService from '../services/NotificationTriggerService';
import { Patient, PatientAppointment, Provider } from '../types';
import { useVoiceCommands } from '../hooks/useVoiceCommands';
import { useSessionManagement } from '../hooks/useSessionManagement';

// Service interface
interface Service {
  id: string;
  code: string;
  description: string;
  rate: number;
  duration: number;
  isDefault: boolean;
  billInUnits: boolean;
  availableOnline: boolean;
  allowNewClients: boolean;
  requireCallToRequest: boolean;
  blockOffBefore: number;
  blockOffAfter: number;
}

// Empty defaults — real data comes from Firestore
const emptyPatients: Patient[] = [];
const emptyPatientAppointments: PatientAppointment[] = [];

const ProviderDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, roles, primaryRole } = useAuth();

  // Create provider object from Firebase user profile
  const provider: Provider = {
    id: userProfile?.uid || '',
    name: userProfile?.displayName || 'Provider',
    email: userProfile?.email || '',
    phone: '',
    specialization: '',
    licenseNumber: '',
    isActive: userProfile?.isActive ?? true,
    createdAt: userProfile?.createdAt?.toDate() || new Date()
  };

  // State declarations — data loaded from Firestore
  const [patients] = useState(emptyPatients);
  const [patientAppointments] = useState(emptyPatientAppointments);
  const [services, setServices] = useState<Service[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'services' | 'billing' | 'clients' | 'meetings' | 'prescriptions' | 'sessions' | 'schedule' | 'analytics' | 'activity' | 'marketing' | 'settings' | 'tasks' | 'inquiries' | 'hr' | 'messages' | 'team-chat' | 'client-activity' | 'emergency-cancellations'>('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [preSelectedPatientForSession, setPreSelectedPatientForSession] = useState<PatientServiceType | null>(null);
  const [preSelectedAppointmentForSession, setPreSelectedAppointmentForSession] = useState<Appointment | null>(null);
  const [activeVideoSession, setActiveVideoSession] = useState<Appointment | null>(null);
  const [activeMeetingVideo, setActiveMeetingVideo] = useState<{ meetingId: string; title: string } | null>(null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [postSessionResult, setPostSessionResult] = useState<{ result: PostSessionResult; appointment: Appointment } | null>(null);
  const [providerTitle, setProviderTitle] = useState<string>('');
  const [titleLoaded, setTitleLoaded] = useState(false);

  // Picovoice Access Key from config
  const PICOVOICE_ACCESS_KEY = PICOVOICE_CONFIG.ACCESS_KEY;

  // Non-clinical roles don't get voice controls or greeting
  // Clinical roles: provider, clinical_director, system_admin
  const CLINICAL_ROLES = ['provider', 'clinical_director', 'system_admin'];
  const isAdminStaffRole = !roles.some(r => CLINICAL_ROLES.includes(r));

  // Custom hooks for business logic
  const {
    wakeWordEnabled,
    speechInitialized,
    initializeSpeech,
    speak,
    speakGreeting,
    handleWakeWordDetected,
    handleVoiceCommand,
    toggleWakeWord,
    disableClinic,
    enableClinic
  } = useVoiceCommands({ providerId: user?.uid, onTabChange: (tab: string) => setActiveTab(tab as any) });

  const {
    sessionActive,
    sessionTime,
    selectedPatient,
    sessionNotes,
    setSessionTime,
    setSessionNotes,
    startSession,
    endSession,
    handleRecordingComplete,
    handleRecordingStart,
    handleRecordingStop,
    handleContentFiltered
  } = useSessionManagement();

  const handleLogout = () => {
    disableClinic(); // Stop Clinic voice on logout
    navigate('/');
  };

  const handleTabChange = (tab: string) => {
    initializeSpeech();
    setActiveTab(tab as any);
  };

  const handleStartSessionFromClient = (patient: PatientServiceType) => {
    setPreSelectedPatientForSession(patient);
    setActiveTab('sessions');
  };

  const handleStartSessionFromAppointment = (appointment: Appointment) => {
    // Start the session tracking
    startSession(appointment.patientName || 'Patient');

    // For video appointments, open in a popup window so dashboard stays accessible
    if (appointment.modality === 'video') {
      disableClinic(); // Silence Clinic voice during video session
      const popupUrl = `/video-session?appointmentId=${encodeURIComponent(appointment.id)}&role=provider`;
      const popup = window.open(
        popupUrl,
        `video-session-${appointment.id}`,
        'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
      );
      if (!popup) {
        // Popup blocked — fall back to inline overlay
        setActiveVideoSession(appointment);
      }
    } else {
      // For in-person appointments, go to sessions tab
      disableClinic(); // Silence Clinic voice during in-person session (Web Speech conflict)
      setPreSelectedAppointmentForSession(appointment);
      setActiveTab('sessions');
    }
  };

  const handleEndVideoSession = async (pipelineResult?: PostSessionResult) => {
    const appointment = activeVideoSession;
    setActiveVideoSession(null);
    enableClinic(); // Restore Clinic voice after session ends
    endSession();

    // Mark appointment as completed in Firestore
    if (appointment?.id) {
      try {
        await firebaseAppointmentService.completeAppointment(appointment.id);
      } catch (err) {
        console.error('Failed to mark appointment as completed:', err);
      }

      // Log session activity so it appears in Practice Activity tab
      try {
        await FirebaseActivityService.getInstance().logActivity({
          providerId: user?.uid || '',
          clientId: appointment.patientId,
          clientName: appointment.patientName || 'Client',
          activityType: 'appointment_completed',
          title: 'Video Session Completed',
          description: `Video therapy session with ${appointment.patientName || 'client'} completed.`,
          metadata: { appointmentId: appointment.id }
        });
      } catch (err) {
        console.error('Failed to log session activity:', err);
      }

      // Send post-session follow-up notification (email + SMS)
      if (appointment.patientId) {
        const aptDate = new Date(appointment.appointmentDate);
        const dateStr = new Date(aptDate.getUTCFullYear(), aptDate.getUTCMonth(), aptDate.getUTCDate())
          .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        NotificationTriggerService.getInstance().sendPostSession({
          patientId: appointment.patientId,
          providerId: user?.uid || '',
          appointmentDate: dateStr,
          appointmentId: appointment.id,
        }).catch(() => {});
      }
    }

    // If pipeline produced results, show the review modal
    if (pipelineResult && appointment) {
      setPostSessionResult({ result: pipelineResult, appointment });
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleClientClick = (clientId: string) => {
    // Navigate to clients tab - the ClientsTab will handle showing the profile
    setActiveTab('clients');
    // TODO: Pass clientId to ClientsTab to open the specific client profile
  };

  // Resolve practice owner ID (for team members, find who invited them)
  const [practiceOwnerId, setPracticeOwnerId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) return;
    // Check if this user is a team member under someone else's practice
    const resolveOwner = async () => {
      try {
        const q = query(collection(db, 'team_members'), where('authUid', '==', user.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const ownerIdFromTeam = snap.docs[0].data().practiceOwnerId;
          if (ownerIdFromTeam && ownerIdFromTeam !== user.uid) {
            setPracticeOwnerId(ownerIdFromTeam);
            return;
          }
        }
      } catch (e) { console.warn('Could not resolve practice owner:', e); }
      // Default: current user is the practice owner
      setPracticeOwnerId(user.uid);
    };
    resolveOwner();
  }, [user?.uid]);

  // Initialize team member service (Firestore-backed)
  useEffect(() => {
    if (practiceOwnerId) {
      const service = TeamMemberService.getInstance();
      service.startListening(practiceOwnerId);
      service.seedDefaultIfEmpty();
      return () => service.stopListening();
    }
  }, [practiceOwnerId]);

  // Initialize company settings service (Firestore-backed)
  useEffect(() => {
    if (practiceOwnerId) {
      const settings = CompanySettingsService.getInstance();
      settings.startListening(practiceOwnerId);
      return () => settings.stopListening();
    }
  }, [practiceOwnerId]);

  // Fetch provider title (e.g. "Dr.") from provider_profiles
  useEffect(() => {
    if (!user?.uid) return;
    const fetchTitle = async () => {
      try {
        const profileDoc = await getDoc(doc(db, 'provider_profiles', user.uid));
        if (profileDoc.exists()) {
          setProviderTitle(profileDoc.data().title || '');
        }
      } catch { /* title is optional — don't block on failure */ }
      setTitleLoaded(true);
    };
    fetchTitle();
  }, [user?.uid]);

  // Clinic greets provider on first interaction with dashboard
  // Chrome blocks speech synthesis until user interacts with the page,
  // so we wait for the first click/keypress to unlock audio, then greet.
  const hasGreetedRef = React.useRef(false);

  // Load greeting text and auto-play once data + roles are ready.
  // The login-page click already unlocked ClinicVoice audio, so we can
  // speak immediately without waiting for another interaction on this page.
  useEffect(() => {
    // Only providers and clinical directors get voice greeting — not admin staff
    if (isAdminStaffRole) return;
    const displayName = userProfile?.displayName || user?.displayName || userProfile?.email?.split('@')[0] || '';
    if (!user?.uid || !displayName || !titleLoaded) return;
    if (hasGreetedRef.current) return;
    // Only greet on fresh login, not on stale auto-auth (page refresh / returning tab)
    if (!sessionStorage.getItem('clinic_fresh_login')) {
      hasGreetedRef.current = true;
      return;
    }
    const announcedKey = `clinic_announced_${new Date().toDateString()}`;
    if (sessionStorage.getItem(announcedKey)) {
      hasGreetedRef.current = true;
      return;
    }

    const loadAndPlay = async () => {
      const firstName = displayName.split(' ')[0] || 'there';
      // Use title (e.g. "Dr.") if set in provider profile, otherwise just first name
      const greetName = providerTitle ? `${providerTitle} ${displayName.split(' ').slice(-1)[0] || firstName}` : firstName;
      const greeting = new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening';
      const isAdminStaff = roles.includes('administrative_staff') && !roles.includes('provider') && !roles.includes('clinical_director') && !roles.includes('system_admin');
      const isSysAdmin = roles.includes('system_admin') && !roles.includes('provider');
      const isClinicalDirector = roles.includes('clinical_director') && !roles.includes('provider');

      let text: string;
      if (isAdminStaff) {
        text = `Good ${greeting}, ${firstName}. Welcome to your dashboard. You can manage schedules, client files, and team coordination from here.`;
      } else if (isSysAdmin) {
        text = `Good ${greeting}, ${greetName}. Welcome back. You can manage your practice, team members, and system settings from your dashboard.`;
      } else if (isClinicalDirector) {
        text = `Good ${greeting}, ${greetName}. Welcome back. You can oversee clinical operations, review team activity, and manage client care from your dashboard.`;
      } else {
        try {
          const now = new Date();
          const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          const appointments = await firebaseAppointmentService.getAppointments({
            providerId: user.uid,
            startDate: startOfDay,
            endDate: endOfDay,
          });
          const active = appointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed');

          // Also check upcoming week
          const endOfWeek = new Date(startOfDay);
          endOfWeek.setDate(endOfWeek.getDate() + 7);
          const weekAppointments = await firebaseAppointmentService.getAppointments({
            providerId: user.uid,
            startDate: endOfDay,
            endDate: endOfWeek,
          });
          const upcomingWeek = weekAppointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed');

          if (active.length === 0 && upcomingWeek.length === 0) {
            text = `Good ${greeting}, ${greetName}. You have no appointments scheduled for today or this week.`;
          } else if (active.length === 0) {
            const nextAppt = upcomingWeek[0];
            const nextDate = new Date(nextAppt.appointmentDate);
            const dayName = nextDate.toLocaleDateString('en-US', { weekday: 'long' });
            text = `Good ${greeting}, ${greetName}. You have no appointments today, but you have ${upcomingWeek.length} coming up this week. Your next one is on ${dayName} with ${nextAppt.patientName || 'a patient'}.`;
          } else {
            let weekNote = '';
            if (upcomingWeek.length > 0) {
              weekNote = ` You also have ${upcomingWeek.length} more this week.`;
            }
            text = `Good ${greeting}, ${greetName}. You have ${active.length} appointment${active.length === 1 ? '' : 's'} today.${weekNote} Say "what are my appointments" for details.`;
          }
        } catch {
          text = `Good ${greeting}, ${greetName}. Welcome to your dashboard.`;
        }
      }

      if (hasGreetedRef.current) return; // Guard against double-fire
      hasGreetedRef.current = true;
      sessionStorage.setItem(announcedKey, 'true');
      sessionStorage.removeItem('clinic_fresh_login'); // Consume the flag
      // Short delay so the page finishes rendering before we speak
      // Don't greet if a video session is already active
      setTimeout(() => { if (!activeVideoSession) speakGreeting(text); }, 800);
    };

    loadAndPlay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, userProfile?.displayName, roles, activeVideoSession, providerTitle, titleLoaded]);

  // Listen for keyboard shortcut navigation events
  useEffect(() => {
    const handleNavigateToTab = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { tab } = customEvent.detail;
      if (tab) {
        setActiveTab(tab);
      }
    };

    window.addEventListener('navigate-to-tab', handleNavigateToTab as EventListener);

    return () => {
      window.removeEventListener('navigate-to-tab', handleNavigateToTab as EventListener);
    };
  }, []);

  // Re-enable Clinic voice when user navigates away from sessions tab
  useEffect(() => {
    if (activeTab !== 'sessions' && !activeVideoSession) {
      enableClinic();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Listen for messages from video session popup window
  useEffect(() => {
    const handlePopupMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'video-session-ended') {
        enableClinic(); // Restore Clinic voice after session ends
        endSession();
      }

      if (event.data.type === 'video-session-closed') {
        enableClinic(); // Restore Clinic voice if popup closed early
        endSession();
      }
    };

    window.addEventListener('message', handlePopupMessage);
    return () => window.removeEventListener('message', handlePopupMessage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        onClientClick={handleClientClick}
        practiceOwnerId={practiceOwnerId || undefined}
      />

      {/* Main Content Area */}
      <div style={{
        marginLeft: sidebarCollapsed ? '80px' : '280px',
        width: `calc(100vw - ${sidebarCollapsed ? '80px' : '280px'})`,
        transition: 'margin-left 0.3s ease, width 0.3s ease',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Header
          user={provider}
          userType="provider"
          onLogout={handleLogout}
          showSessionStatus={true}
          sessionActive={sessionActive}
          onOpenProfileSettings={() => setShowProfileSettings(true)}
          onOpenSettings={
            roles.some(r => ['system_admin', 'clinical_director'].includes(r))
              ? () => setActiveTab('settings')
              : undefined
          }
        />

        {/* Main Page Content */}
        <main id="main-content" style={{
          flex: 1,
          overflow: 'auto',
          padding: '2rem',
          backgroundColor: '#16325B'
        }}>
          {/* Welcome Header and Session Timer */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            marginBottom: '2rem'
          }}>
            <div>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#F7E9A0',
                margin: '0 0 0.5rem 0'
              }}>
                Welcome, {providerTitle ? `${providerTitle} ${provider.name.split(' ').slice(-1)[0]}` : provider.name}!
              </h1>
              <p style={{
                fontSize: '1.125rem',
                color: '#e5e7eb',
                margin: '0 0 8px 0'
              }}>
                {roles.includes('administrative_staff')
                  ? 'Coordinate schedules, manage patient files, and support your team'
                  : roles.includes('system_admin') && !roles.includes('provider')
                  ? 'Manage your practice, team members, and system settings'
                  : 'Manage your patients, schedule appointments, and conduct sessions'
                }
              </p>
              {roles.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {roles.map(role => (
                    <span key={role} style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      backgroundColor: role === 'system_admin' ? 'rgba(239,68,68,0.2)' :
                        role === 'clinical_director' ? 'rgba(212,160,23,0.2)' :
                        role === 'hr_admin' ? 'rgba(139,92,246,0.2)' :
                        role === 'provider' ? 'rgba(16,185,129,0.2)' :
                        role === 'administrative_staff' ? 'rgba(59,130,246,0.2)' : 'rgba(107,114,128,0.2)',
                      color: role === 'system_admin' ? '#FCA5A5' :
                        role === 'clinical_director' ? '#F7E9A0' :
                        role === 'hr_admin' ? '#C4B5FD' :
                        role === 'provider' ? '#6EE7B7' :
                        role === 'administrative_staff' ? '#93C5FD' : '#D1D5DB',
                      border: `1px solid ${
                        role === 'system_admin' ? 'rgba(239,68,68,0.3)' :
                        role === 'clinical_director' ? 'rgba(212,160,23,0.3)' :
                        role === 'hr_admin' ? 'rgba(139,92,246,0.3)' :
                        role === 'provider' ? 'rgba(16,185,129,0.3)' :
                        role === 'administrative_staff' ? 'rgba(59,130,246,0.3)' : 'rgba(107,114,128,0.3)'
                      }`
                    }}>
                      {role === 'system_admin' ? 'System Admin' :
                       role === 'clinical_director' ? 'Clinical Director' :
                       role === 'hr_admin' ? 'HR Admin' :
                       role === 'provider' ? 'Provider' :
                       role === 'administrative_staff' ? 'Admin Staff' : role}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Voice Controls and Session Timer — providers only */}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
              {!isAdminStaffRole && (
                <>
                  <VoiceControls
                    accessKey={PICOVOICE_ACCESS_KEY}
                    wakeWordEnabled={wakeWordEnabled}
                    speechInitialized={speechInitialized}
                    onWakeWordDetected={handleWakeWordDetected}
                    onVoiceCommand={handleVoiceCommand}
                    onWakeWordToggle={toggleWakeWord}
                    onInitializeSpeech={initializeSpeech}
                  />
                  {/* Floating — dictation starts by voice from anywhere in the app,
                      so the panel cannot live inside one editor. Renders nothing
                      until a session is running. */}
                  <DictationPanel />
                </>
              )}

              {/* Session Timer Widget */}
              {sessionActive && (
                <div style={{
                  backgroundColor: '#28527A',
                  borderRadius: '8px',
                  padding: '1rem',
                  minWidth: '200px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                  border: '1px solid #6CA6C1'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#D4A017', fontWeight: '600' }}>
                      Active Session
                    </span>
                  </div>
                  {selectedPatient && (
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#e5e7eb' }}>
                      with {selectedPatient}
                    </p>
                  )}
                  <SessionTimer
                    isActive={sessionActive}
                    onTimeUpdate={setSessionTime}
                    showControls={false}
                  />
                  <button
                    onClick={endSession}
                    style={{
                      width: '100%',
                      marginTop: '0.5rem',
                      fontSize: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#EF4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    End Session
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <OverviewTab
              patients={patients}
              patientAppointments={patientAppointments}
              staffMeetings={[]}
              sessionActive={sessionActive}
              onTabChange={(tab: 'overview' | 'schedule' | 'meetings' | 'prescriptions' | 'sessions' | 'clients') => setActiveTab(tab)}
              roles={roles}
              practiceOwnerId={practiceOwnerId || undefined}
            />
          )}

          {activeTab === 'services' && (
            <ServicesTab services={services} setServices={setServices} />
          )}

          {activeTab === 'billing' && (
            <BillingTab />
          )}

          {activeTab === 'clients' && (
            <ClientsTab onStartSession={handleStartSessionFromClient} onScheduleAppointment={() => setActiveTab('schedule')} onMessageClient={() => setActiveTab('messages')} />
          )}

          {activeTab === 'meetings' && (
            <MeetingsTab
              practiceOwnerId={practiceOwnerId || provider.id}
              onJoinMeetingVideo={(meetingId, title) => {
                setActiveMeetingVideo({ meetingId, title });
              }}
            />
          )}

          {activeTab === 'activity' && (
            <ActivityTab />
          )}

          {activeTab === 'prescriptions' && (
            <PrescriptionsPage userType="provider" currentUser={provider} providerId={provider.id} />
          )}

          {activeTab === 'sessions' && (
            <SessionsTab
              preSelectedPatient={preSelectedPatientForSession}
              preSelectedAppointment={preSelectedAppointmentForSession}
            />
          )}

          {activeTab === 'schedule' && (
            <ScheduleTab
              providerId={provider.id}
              showAllProviders={roles.some(r => ['system_admin', 'clinical_director', 'administrative_staff'].includes(r))}
              onStartSession={handleStartSessionFromAppointment}
            />
          )}

          {activeTab === 'analytics' && (
            <AnalyticsTab />
          )}

          {activeTab === 'marketing' && (
            <MarketingTab />
          )}

          {activeTab === 'settings' && (
            <div style={{ height: '80vh' }}>
              <SettingsPageNew />
            </div>
          )}

          {activeTab === 'tasks' && (
            <TasksTab practiceOwnerId={practiceOwnerId || provider.id} />
          )}

          {activeTab === 'inquiries' && (
            <InquiriesTab />
          )}

          {activeTab === 'hr' && (
            <HRTab
              providerId={practiceOwnerId || provider.id}
              providerName={provider.name}
            />
          )}

          {activeTab === 'messages' && (
            <div style={{ height: 'calc(100vh - 200px)' }}>
              <MessagesTab />
            </div>
          )}

          {activeTab === 'team-chat' && (
            <div style={{ height: 'calc(100vh - 200px)' }}>
              <TeamChatTab />
            </div>
          )}

          {activeTab === 'client-activity' && (
            <div style={{ height: 'calc(100vh - 200px)' }}>
              <ClientActivityTab />
            </div>
          )}

          {activeTab === 'emergency-cancellations' && (
            <div style={{ height: 'calc(100vh - 200px)' }}>
              <EmergencyCancellationsTab />
            </div>
          )}

        </main>
      </div>

      {/* Video Session — now opens in a popup window (see handleStartSessionFromAppointment).
         Fallback inline overlay kept for popup-blocked scenarios. */}
      {activeVideoSession && (
        <VideoSessionWhereby
          appointmentId={activeVideoSession.id}
          patientId={activeVideoSession.patientId}
          patientName={activeVideoSession.patientName || 'Patient'}
          patient2Id={(activeVideoSession as any).patient2Id}
          patient2Name={(activeVideoSession as any).patient2Name}
          providerId={provider.id}
          providerName={provider.name}
          role="provider"
          currentUserId={user?.uid}
          onEndSession={handleEndVideoSession}
        />
      )}

      {/* Staff Meeting Video - Same Whereby video, no post-session pipeline */}
      {activeMeetingVideo && (
        <VideoSessionWhereby
          appointmentId={`meeting-${activeMeetingVideo.meetingId}`}
          patientId=""
          patientName={activeMeetingVideo.title}
          providerId={provider.id}
          providerName={provider.name}
          role="provider"
          currentUserId={user?.uid}
          onEndSession={() => setActiveMeetingVideo(null)}
        />
      )}

      {/* Post-Session Review Modal */}
      {postSessionResult && (
        <PostSessionReviewModal
          result={postSessionResult.result}
          appointmentId={postSessionResult.appointment.id}
          patientId={postSessionResult.appointment.patientId}
          patientName={postSessionResult.appointment.patientName || 'Patient'}
          providerId={provider.id}
          onClose={() => setPostSessionResult(null)}
          onNoteSaved={() => console.log('[PostSession] Clinical note saved from AI review')}
        />
      )}

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <ProfileSettings onClose={() => setShowProfileSettings(false)} />
      )}

      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>

    </div>
  );
};

export default ProviderDashboard;